import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import SimpleNavbar from './SimpleNavbar';
import Footer from './Footer';
import ChatBubbleForUser19 from '../chat/ChatBubbleForUser19';
import DocumentationHandle from '../common/DocumentationHandle';
import { HelpModal, helpContent } from '../help';
import { API_BASE_URL } from '../../config';
import { getSettings } from '../../models/settingsModel';
import { getProcurementSettings } from '../../models/procurementModel';
import BiddingPanel from '../../views/pages/Bidding/BiddingPanel';
import { useAuth } from '../../context/AuthContext';
 

function DashboardLayout() {
  const location = useLocation();
  const { can, isProcurement } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [siteName, setSiteName] = useState(null);
  const [showBidding, setShowBidding] = useState(false);
  const [biddingEnabledSetting, setBiddingEnabledSetting] = useState(false);
  
  // Check if user has bidding management permissions
  const canManageBidding = isProcurement() || can('Create Bidding Item') || can('Edit Bidding Item') || can('Manage Bidding Categories');
  
  // Show bidding handle if user has permission OR if the setting is enabled for regular users
  const showBiddingHandle = canManageBidding || biddingEnabledSetting;

  // Fetch current version and site settings
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch version
        const response = await fetch(`${API_BASE_URL}/api/updates/history`);
        const data = await response.json();
        if (data.status === 1) {
          const installed = data.data.updates || data.data.history || [];
          if (installed.length > 0) {
            const latest = installed.sort((a, b) => new Date(b.installed_at) - new Date(a.installed_at))[0];
            setCurrentVersion(latest.version);
          }
        }
        
        // Fetch site name from settings
        const settingsResponse = await fetch(`${API_BASE_URL}/api/settings`);
        const settingsData = await settingsResponse.json();
        if (settingsData.status === 1) {
          setSiteName(settingsData.data.site_name);
        }

        // Fetch procurement settings for bidding visibility
        const procurementSettings = await getProcurementSettings();
        if (procurementSettings && procurementSettings.site_show_bidding === '1') {
          setBiddingEnabledSetting(true);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const toggleHelp = () => {
    setHelpOpen(!helpOpen);
  };

  // Get current help content based on location
  const getCurrentHelpContent = () => {
    const path = location.pathname;
    return helpContent[path] || helpContent['default'];
  };

  // Auto-collapse sidebar when navigating to chat routes
  useEffect(() => {
    if (location.pathname.startsWith('/chats')) {
      setSidebarCollapsed(true);
    }
  }, [location.pathname]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+H or F1 to open help
      if ((event.ctrlKey && event.key === 'h') || event.key === 'F1') {
        event.preventDefault();
        setHelpOpen(true);
      }
      // Escape to close help modal
      if (event.key === 'Escape') {
        setHelpOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Overlay for mobile when sidebar is open */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        toggleSidebar={() => setSidebarOpen(!sidebarOpen)} 
        isCollapsed={sidebarCollapsed} 
        toggleSidebarCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Sidebar Collapse Toggle Button - Fixed position when sidebar is collapsed */}
      {sidebarCollapsed && (
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="fixed top-4 left-4 z-30 p-2 bg-green-600 text-white rounded-lg shadow-lg hover:bg-green-700 transition-colors lg:block hidden"
          title="Expand Sidebar"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h8m-8 6h16"></path>
          </svg>
        </button>
      )}

      {/* Main content area */}
      <div className={`flex-1 flex flex-col h-full transition-all duration-300 min-w-0 ${sidebarCollapsed ? 'lg:ml-0' : ''}`}>
        {/* Simple Professional Navbar */}
        <SimpleNavbar 
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
        />
        
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-neutral-50 p-4">
          <div className="h-full">
            <Outlet />
          </div>
        </main>
        
        <Footer currentVersion={currentVersion} siteName={siteName} />

        {/* Help Modal */}
        <HelpModal
          isOpen={helpOpen}
          onClose={() => setHelpOpen(false)}
          title={getCurrentHelpContent().title}
        >
          {getCurrentHelpContent().content}
        </HelpModal>
      </div>

      {/* Global floating helpdesk chat bubble (only for user ID 19) */}
      <ChatBubbleForUser19 />

      {/* Context-aware documentation handle */}
      <DocumentationHandle />

      {/* Bidding Button - Always visible for users with bidding permissions, otherwise based on setting */}
      {showBiddingHandle && (
        <div 
          className="fixed right-0 top-1/2 transform -translate-y-1/2 z-50"
        >
          <button 
            className="bg-[#0087D4] text-white px-1.5 py-3 rounded-l-xl shadow-2xl hover:bg-[#0076bb] transition-all duration-300 border-l border-t border-b border-white/20 flex flex-col items-center justify-center gap-1"
            onClick={() => setShowBidding(true)}
            title="Bidding"
          >
            <span className="text-xs font-bold tracking-tight leading-tight" style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}>BIDDING</span>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </button>
        </div>
      )}

      <BiddingPanel show={showBidding} onHide={() => setShowBidding(false)} />
    </div>
  );
}

export default DashboardLayout;